import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, ChevronDown, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/lib/settings";
import { api, buildWhatsAppUrl, generalWhatsAppMessage, normalizeProduct, normalizeTaxonomy } from "@/lib/api";
import { useUIOverlay } from "@/lib/uiOverlay";

const STATIC_NAV = [
  { label: "Beranda", to: "/" },
  { label: "Tentang Kami", to: "/tentang-kami" },
  { label: "Hubungi Kami", to: "/hubungi-kami" },
];

function CategoryColumn({ cat, products, onNavigate }) {
  const items = products.filter((p) => p.category === cat.name).slice(0, 6);
  const href = `/produk?category=${encodeURIComponent(cat.name)}`;
  return (
    <div>
      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF9418] mb-2 border-b border-[#F0F0F0] pb-2">{cat.name}</div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.slug}>
            <Link to={`/produk/${it.slug}`} onClick={onNavigate} className="text-sm text-[#404040] hover:text-[#FF9418] block" data-testid={`mega-produk-${it.slug}`}>
              {it.product_name}
            </Link>
          </li>
        ))}
        {items.length === 0 && <li className="text-xs text-[#A3A3A3] italic">Belum ada produk</li>}
      </ul>
      <Link to={href} onClick={onNavigate} className="mt-3 pt-2 border-t border-[#F0F0F0] flex items-center gap-1 text-xs font-mono-tech uppercase tracking-wider text-[#FF9418] hover:text-[#E9840D] font-semibold" data-testid={`mega-viewall-${cat.slug}`}>
        Lihat semua {cat.name} <ArrowRight size={11} />
      </Link>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [brandMegaOpen, setBrandMegaOpen] = useState(false);
  const [mobileProdukOpen, setMobileProdukOpen] = useState(false);
  const [mobileBrandOpen, setMobileBrandOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [menuCategories, setMenuCategories] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [productList, setProductList] = useState([]);
  const nav = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const waNumber = settings?.whatsapp;
  const { setMenuOpen } = useUIOverlay();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Publish mobile menu open state to the overlay context so floating
  // controls (WhatsApp, sticky product CTA) can hide while the menu is up.
  useEffect(() => { setMenuOpen(open); }, [open, setMenuOpen]);

  // Dynamic navigation source — refetch when route changes so admin edits reflect immediately.
  useEffect(() => {
    api.get("/categories", { params: { scope: "menu" } })
      .then((r) => setMenuCategories((r.data || []).map((c) => normalizeTaxonomy(c, "category"))))
      .catch(() => setMenuCategories([]));
    api.get("/brands")
      .then((r) => setBrandList((r.data || []).map((b) => normalizeTaxonomy(b, "brand"))))
      .catch(() => setBrandList([]));
    api.get("/products", { params: { limit: 100, sort: "name_asc" } })
      .then((r) => setProductList((r.data || []).map(normalizeProduct)))
      .catch(() => setProductList([]));
  }, [location.pathname]);

  useEffect(() => {
    setOpen(false); setMegaOpen(false); setBrandMegaOpen(false); setMobileProdukOpen(false); setMobileBrandOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) nav(`/produk?q=${encodeURIComponent(searchQ.trim())}`);
    setOpen(false);
  };
  const closeMega = () => { setMegaOpen(false); setBrandMegaOpen(false); };
  const closeMobile = () => setOpen(false);

  // Show every eligible category (published + show_in_menu) — no artificial slice.
  const megaColumns = menuCategories;
  // Widen the mega panel when there are more than 3 categories so the grid can wrap comfortably.
  const megaPanelWidth = megaColumns.length > 3 ? "w-[960px]" : "w-[720px]";

  return (
    <header className={`sticky top-0 z-50 bg-white border-b border-[#E5E5E5] ${scrolled ? "header-scrolled" : ""}`} data-testid="site-header">
      {/* Main header row: mobile stays SINGLE row [logo | WA | hamburger] */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 md:gap-4 lg:gap-6 flex-nowrap">
        <Link to="/" className="flex items-center shrink-0 mr-auto lg:mr-0" data-testid="logo-link">
          <img src="/logo.png" alt="Teknik Diesel Indonesia" className="h-10 md:h-14 lg:h-16 w-auto" style={{ maxWidth: "240px" }} />
        </Link>

        <nav className="hidden lg:flex items-center gap-0 flex-1 justify-center">
          <NavLink to="/" className={({isActive}) => `px-3 py-2 text-sm font-semibold relative transition ${isActive ? "text-[#FF9418]" : "text-[#171717] hover:text-[#FF9418]"}`} data-testid="nav-beranda">
            {({isActive}) => <>Beranda{isActive && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#FF9418]" />}</>}
          </NavLink>
          <div className="relative" onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}>
            <NavLink to="/produk" className={({isActive}) => `px-3 py-2 text-sm font-semibold flex items-center gap-1 relative transition ${isActive ? "text-[#FF9418]" : "text-[#171717] hover:text-[#FF9418]"}`} data-testid="nav-produk">
              {({isActive}) => <>Produk <ChevronDown size={14} />{isActive && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#FF9418]" />}</>}
            </NavLink>
            {megaOpen && megaColumns.length > 0 && (
              <div className={`absolute left-0 top-full pt-2 ${megaPanelWidth}`} data-testid="mega-menu">
                <div className="bg-white border border-[#E5E5E5] shadow-xl rounded-lg overflow-hidden">
                  <div className="p-6 grid grid-cols-3 gap-x-6 gap-y-6">
                    {megaColumns.map((c) => <CategoryColumn key={c.id || c.slug} cat={c} products={productList} onNavigate={closeMega} />)}
                  </div>
                  <Link to="/produk" onClick={closeMega} className="block bg-[#F8F8F8] hover:bg-[#FFF4E8] border-t border-[#E5E5E5] py-3 text-center font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] hover:text-[#E9840D] font-bold transition group" data-testid="mega-viewall-produk">
                    Lihat Semua Produk <ArrowRight size={12} className="inline transition group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            )}
          </div>
          <div className="relative" onMouseEnter={() => setBrandMegaOpen(true)} onMouseLeave={() => setBrandMegaOpen(false)}>
            <button className="px-3 py-2 text-sm font-semibold flex items-center gap-1 text-[#171717] hover:text-[#FF9418]" data-testid="nav-brand">
              Brand <ChevronDown size={14} />
            </button>
            {brandMegaOpen && brandList.length > 0 && (
              <div className="absolute left-0 top-full pt-2 w-[280px]" data-testid="mega-brand-menu">
                <div className="bg-white border border-[#E5E5E5] shadow-xl rounded-lg overflow-hidden py-2">
                  {brandList.filter((b) => b.name).map((b) => (
                    <Link key={b.id || b.slug} to={`/brand/${b.slug}`} onClick={closeMega} className="block px-4 py-2 text-sm font-semibold text-[#171717] hover:bg-[#FFF4E8] hover:text-[#FF9418]" data-testid={`mega-brand-${b.slug}`}>
                      {b.name} <span className="text-[10px] text-[#A3A3A3] font-mono-tech ml-1">({b.count})</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {STATIC_NAV.slice(1).map((item) => (
            <NavLink key={item.to} to={item.to} className={({isActive}) => `px-3 py-2 text-sm font-semibold relative transition ${isActive ? "text-[#FF9418]" : "text-[#171717] hover:text-[#FF9418]"}`} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g,'-')}`}>
              {({isActive}) => <>{item.label}{isActive && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#FF9418]" />}</>}
            </NavLink>
          ))}
        </nav>

        {/* Desktop search + WA */}
        <form onSubmit={submitSearch} className="hidden md:flex items-center bg-white rounded-lg border border-[#E5E5E5] focus-within:border-[#FF9418] transition">
          <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Cari produk..." className="bg-transparent px-3 py-2 text-sm w-36 outline-none text-[#171717] placeholder:text-[#A3A3A3]" data-testid="header-search-input" />
          <button type="submit" className="px-2 py-2 text-[#737373] hover:text-[#FF9418]" data-testid="header-search-button"><Search size={16} /></button>
        </form>
        {waNumber && (
          <a href={buildWhatsAppUrl(waNumber, generalWhatsAppMessage())} target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex" data-testid="header-whatsapp-cta">
            <Button className="bg-[#FF9418] hover:bg-[#E9840D] text-white font-semibold rounded-lg h-10 px-5 shadow-sm">
              <MessageCircle size={16} /> WhatsApp
            </Button>
          </a>
        )}

        {/* MOBILE right controls: single row, no wrap */}
        {waNumber && (
          <a href={buildWhatsAppUrl(waNumber, generalWhatsAppMessage())} target="_blank" rel="noopener noreferrer" className="md:hidden inline-flex shrink-0" aria-label="WhatsApp" data-testid="mobile-whatsapp-cta">
            <button type="button" className="w-10 h-10 rounded-lg bg-[#22C55E] hover:bg-[#16A34A] text-white flex items-center justify-center shadow-sm">
              <MessageCircle size={18} />
            </button>
          </a>
        )}
        <button onClick={() => setOpen(!open)} className="lg:hidden text-[#171717] p-1 shrink-0" data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="lg:hidden border-t border-[#E5E5E5] bg-white max-h-[85vh] overflow-y-auto">
          <form onSubmit={submitSearch} className="p-4 border-b border-[#F0F0F0]">
            <div className="flex items-center bg-white rounded-lg border border-[#E5E5E5] focus-within:border-[#FF9418]">
              <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Cari produk, brand, atau model..." className="bg-transparent px-3 py-2.5 text-sm flex-1 outline-none text-[#171717]" data-testid="mobile-search-input" />
              <button type="submit" className="px-3 py-2 text-[#FF9418]" data-testid="mobile-search-submit"><Search size={16} /></button>
            </div>
          </form>
          <nav className="pb-4">
            <NavLink to="/" onClick={closeMobile} className="block px-4 py-3 text-sm font-semibold text-[#171717] hover:text-[#FF9418]" data-testid="mobile-nav-beranda">Beranda</NavLink>

            {/* Produk — categories only, dynamic */}
            <div className="border-t border-[#F0F0F0]">
              <button onClick={() => setMobileProdukOpen(!mobileProdukOpen)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#171717] hover:text-[#FF9418]" data-testid="mobile-nav-produk-toggle">
                <span>Produk</span>
                <ChevronDown size={16} className={`transition ${mobileProdukOpen ? "rotate-180 text-[#FF9418]" : ""}`} />
              </button>
              {mobileProdukOpen && (
                <div className="bg-[#F8F8F8] border-t border-[#F0F0F0]" data-testid="mobile-produk-accordion">
                  {menuCategories.length === 0 && <div className="px-6 py-3 text-xs text-[#A3A3A3] italic">Belum ada kategori</div>}
                  {menuCategories.map((c) => (
                    <Link key={c.id || c.slug} to={`/produk?category=${encodeURIComponent(c.name)}`} onClick={closeMobile} className="flex items-center justify-between px-6 py-3 text-sm text-[#171717] hover:text-[#FF9418] border-b border-[#E5E5E5] last:border-b-0" data-testid={`mobile-cat-${c.slug}`}>
                      <span>{c.name}</span>
                      <span className="text-[10px] font-mono-tech text-[#A3A3A3]">({c.count})</span>
                    </Link>
                  ))}
                  <Link to="/produk" onClick={closeMobile} className="block bg-[#FFF4E8] hover:bg-[#FFD3A2] py-3 text-center font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] font-bold transition" data-testid="mobile-viewall-produk">
                    Lihat Semua Produk <ArrowRight size={12} className="inline" />
                  </Link>
                </div>
              )}
            </div>

            {/* Brand — dynamic */}
            <div className="border-t border-[#F0F0F0]">
              <button onClick={() => setMobileBrandOpen(!mobileBrandOpen)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#171717] hover:text-[#FF9418]" data-testid="mobile-nav-brand-toggle">
                <span>Brand</span>
                <ChevronDown size={16} className={`transition ${mobileBrandOpen ? "rotate-180 text-[#FF9418]" : ""}`} />
              </button>
              {mobileBrandOpen && (
                <div className="bg-[#F8F8F8] border-t border-[#F0F0F0]" data-testid="mobile-brand-accordion">
                  {brandList.filter((b) => b.name).map((b) => (
                    <Link key={b.id || b.slug} to={`/brand/${b.slug}`} onClick={closeMobile} className="flex items-center justify-between px-6 py-3 text-sm text-[#171717] hover:text-[#FF9418] border-b border-[#E5E5E5] last:border-b-0" data-testid={`mobile-brand-${b.slug}`}>
                      <span className="font-semibold">{b.name}</span>
                      <span className="text-[10px] font-mono-tech text-[#A3A3A3]">({b.count})</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {STATIC_NAV.slice(1).map((item) => (
              <NavLink key={item.to} to={item.to} onClick={closeMobile} className="block px-4 py-3 text-sm font-semibold text-[#171717] border-t border-[#F0F0F0] hover:text-[#FF9418]" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g,'-')}`}>
                {item.label}
              </NavLink>
            ))}

            {waNumber && (
              <a href={buildWhatsAppUrl(waNumber, generalWhatsAppMessage())} target="_blank" rel="noopener noreferrer" className="block mx-4 mt-4">
                <Button className="w-full bg-[#FF9418] hover:bg-[#E9840D] text-white font-semibold rounded-lg h-11" data-testid="mobile-wa-cta">
                  <MessageCircle size={16} /> Hubungi via WhatsApp
                </Button>
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
